import { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import { RouteInfo } from "@/components/maps/RouteView"
import { AvoidType, CarRouteConfig, LoadType } from "@/api/adHocCommands"
import {
  VEHICLE_TYPE_NAMES,
  VehicleType,
} from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import { v4 as uuid } from "uuid"
import i18n from "@/lib/i18n/i18n"
import { getIntlLocale } from "@/lib/i18n/format"
import { AlternativeRoute, PerLegAlternatives } from "@/lib/planRouteParser"

export interface AutoSavedRoute {
  id: string
  name: string
  timestamp: number
  temporaryRoute: Route2g
  temporaryRouteInfo?: RouteInfo
  selectedAvoidOptions: AvoidType[]
  selectedLoadType: LoadType[]
  selectedVehicleType: VehicleType
  messageText?: string
  // Document information (for routes created from documents)
  fileName?: string
  fileUrl?: string
  extractedData?: any
  currentStep?: string // 'verify' | 'map'
  driverCarConfig?: CarRouteConfig
  // Average fuel consumption (l/100km) entered on top of the vehicle preset
  fuelConsumption?: string
  // Alternative routes
  alternativeRoutes?: AlternativeRoute[]
  perLegAlternatives?: PerLegAlternatives[]
}

const STORAGE_KEY = "auto_saved_route_plans"
export const MAX_SAVED_ROUTES = 20

export function generateRouteName(stops: RouteStop[]): string {
  if (!stops || stops.length === 0) {
    return i18n.t("maps.saveRoutes.defaultName", {
      date: new Date().toLocaleString(getIntlLocale(undefined)),
    })
  }

  const firstStop = stops[0]
  const lastStop = stops[stops.length - 1]

  const getLocationName = (stop: RouteStop): string => {
    const location = stop.location

    if (location.companyName) {
      return location.companyName
    }

    if (location.countryCode && location.locality) {
      return `${location.countryCode}, ${location.postalCode ? location.postalCode : ""} ${location.locality}`
    }

    if (location.lat && location.lon) {
      const lat = parseFloat(location.lat).toFixed(5)
      const lon = parseFloat(location.lon).toFixed(5)
      return `${lat}, ${lon}`
    }

    if (location.formattedAddress) {
      const parts = location.formattedAddress.split(",")
      if (parts.length >= 2) {
        return parts[parts.length - 2].trim()
      }
      return location.formattedAddress.substring(0, 20) + "..."
    }

    return i18n.t("maps.saveRoutes.unknownLocation")
  }

  const firstName = getLocationName(firstStop)
  const lastName = getLocationName(lastStop)

  if (firstName === lastName) {
    return `${firstName} - ${new Date().toLocaleDateString(getIntlLocale(undefined))}`
  }

  return `${firstName} → ${lastName}`
}

// A stop is identified by its coordinates (rounded to about a metre) or,
// when it has none, by its address.
const stopSignature = (stop: RouteStop): string => {
  const { lat, lon, formattedAddress, companyName, locality } = stop.location
  if (lat && lon) {
    return `${parseFloat(lat).toFixed(5)},${parseFloat(lon).toFixed(5)}`
  }
  return formattedAddress ?? [companyName, locality].filter(Boolean).join(" ")
}

// The same stops in the same order, planned for the same vehicle, are one
// route in the history. Routes are matched by their content rather than by
// the planner session they were saved from: editing the addresses in place
// adds a new entry instead of overwriting the previous one, while checking
// the same route again only moves it back to the top.
export const routeSignature = (
  stops: RouteStop[],
  vehicleType: VehicleType,
): string => `${vehicleType}|${stops.map(stopSignature).join(">")}`

const uniqueName = (routes: AutoSavedRoute[], baseName: string): string => {
  let name = baseName
  let counter = 1
  while (routes.some((route) => route.name === name)) {
    name = `${baseName} (${counter})`
    counter++
  }
  return name
}

// localStorage has a hard quota (usually 5 MB) and a route with all its
// alternatives can be large. Rather than losing the newest route when the
// quota is hit, drop the oldest entries until the list fits.
const persist = (routes: AutoSavedRoute[]): void => {
  let toStore = routes
  for (;;) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
      return
    } catch (error) {
      if (toStore.length <= 1) throw error
      toStore = toStore.slice(0, -1)
    }
  }
}

export function saveRoute(
  temporaryRoute: Route2g,
  temporaryRouteInfo: RouteInfo | undefined,
  selectedAvoidOptions: AvoidType[],
  selectedLoadType: LoadType[],
  selectedVehicleType: VehicleType,
  messageText?: string,
  options?: {
    fileName?: string
    fileUrl?: string
    extractedData?: any
    currentStep?: string
    customName?: string
    driverCarConfig?: CarRouteConfig
    fuelConsumption?: string
    alternativeRoutes?: AlternativeRoute[]
    perLegAlternatives?: PerLegAlternatives[]
  },
): string {
  try {
    const routes = getSavedRoutes()
    const {
      fileName,
      fileUrl,
      extractedData,
      currentStep,
      customName,
      driverCarConfig,
      fuelConsumption,
      alternativeRoutes,
      perLegAlternatives,
    } = options || {}

    const signature = routeSignature(temporaryRoute.stops, selectedVehicleType)
    const existingRoute = routes.find(
      (route) =>
        routeSignature(
          route.temporaryRoute?.stops ?? [],
          route.selectedVehicleType,
        ) === signature,
    )

    const routeId =
      existingRoute?.id ??
      `auto_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    const routeName =
      customName ||
      existingRoute?.name ||
      uniqueName(
        routes,
        `[${VEHICLE_TYPE_NAMES[selectedVehicleType]}] - ${generateRouteName(temporaryRoute.stops)}`,
      )

    const newRoute: AutoSavedRoute = {
      id: routeId,
      name: routeName,
      timestamp: Date.now(),
      temporaryRoute: {
        ...temporaryRoute,
        stops: temporaryRoute.stops.map((stop) => ({
          ...stop,
          action:
            stop.action === "from_driver_location" ? "other" : stop.action,
          by: undefined, // Remove the by field
        })),
      },
      temporaryRouteInfo,
      selectedAvoidOptions,
      selectedLoadType,
      selectedVehicleType,
      messageText,
      fileName,
      fileUrl,
      extractedData,
      currentStep,
      driverCarConfig,
      fuelConsumption,
      alternativeRoutes,
      perLegAlternatives,
    }

    // The saved (or re-saved) route always goes to the top, so the list
    // reads as "last checked first".
    const updatedRoutes = [
      newRoute,
      ...routes.filter((route) => route.id !== routeId),
    ].slice(0, MAX_SAVED_ROUTES)

    persist(updatedRoutes)

    return routeId
  } catch (error) {
    console.error("Failed to auto-save route:", error)
    return ""
  }
}

export function getSavedRoutes(): AutoSavedRoute[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []

    const routes = JSON.parse(stored) as AutoSavedRoute[]
    if (!Array.isArray(routes)) return []
    // Sort by timestamp descending (most recent first)
    return routes.sort((a, b) => b.timestamp - a.timestamp)
  } catch (error) {
    console.error("Failed to load auto-saved routes:", error)
    return []
  }
}

export function loadSavedRoute(routeId: string): AutoSavedRoute | null {
  try {
    const routes = getSavedRoutes()
    return routes.find((route) => route.id === routeId) || null
  } catch (error) {
    console.error("Failed to load auto-saved route:", error)
    return null
  }
}

export function generateSessionId(): string {
  return `session_${Date.now()}_${uuid()}`
}
